import { Add, Remove } from '@mui/icons-material';
import { Box, InputAdornment, Typography } from '@mui/material';
import { increment } from 'firebase/firestore';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import cancelTransaction from '../../api/wallets/cancelTransaction';
import { Cafe, Wallet, WalletLog } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormAutocomplete from '../../components/forms/FormAutocomplete';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSelect from '../../components/forms/FormSelect';
import FormTextField from '../../components/forms/FormTextField';
import { getWalletLogComment } from '../../components/History/WalletHistory';
import TransactionCategories, { transactionCategories } from '../../constants/transactionCategories';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { createBatch } from '../../utils/api';
import getBankCommission from '../../utils/getBankCommission';

const icons = {
  income: <Add />,
  spending: <Remove />,
  transfer: '',
  cancel: '',
};

type Form = {
  target: string | null;
  category: string;
  bankCommission: number | null;
  comment: string;
  sum: string | number;
};

interface Props {
  wallet: DbDocument<Wallet>;
  onClose: () => void;
  type?: 'income' | 'spending' | 'transfer';
  log?: DbDocument<WalletLog>;
}

const EventModal = (props: Props) => {
  const { type, wallet, onClose, log } = props;
  const { cafeSlug, getCurrency, cafe } = useAdmin();
  const { wallets, walletsMap, worker } = useCafe();
  const { lang } = useLocale();
  const workerId = worker?.id || '';
  const workerName = worker?.name || '';

  const form = useForm<Form>({
    mode: 'onChange',
    defaultValues: {
      target: null,
      category: log?.category || (type === 'transfer' ? TransactionCategories.transfer : ''),
      bankCommission: log?.bankCommission || 0,
      comment: log?.comment || '',
      sum: (log?.sum && (log.sum > 0 ? log.sum : -log.sum)) || 0,
    },
  });

  const sum = form.watch('sum');
  const target = form.watch('target');

  const targetWallet = target ? walletsMap[target] : null;
  const bankPercent = targetWallet ? targetWallet.bankPercent : wallet.bankPercent;

  const saveAction = useCallback(
    async (value: Form) => {
      const { target, comment, category, bankCommission } = value;
      const sum = Number(value.sum);
      const batch = createBatch();

      if (log) {
        batch.update(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${log.id}`, { comment, category });
      } else if (type === 'transfer' && target) {
        const newLog: WalletLog = {
          time: new Date().getTime(),
          sum: -sum,
          workerId,
          workerName,
          category,
          bankCommission: 0,
          wallet: wallet.id,
          total: wallet.balance - sum,
          info: { type: 'transfer', direction: 'to', walletName: walletsMap[target]?.name || '', walletId: target },
          comment: '',
          type: 'transfer',
        };
        const targetLog: WalletLog = {
          time: new Date().getTime(),
          sum,
          workerId,
          workerName,
          category,
          bankCommission,
          wallet: target,
          total: (walletsMap[target]?.balance || 0) + sum - (bankCommission || 0),
          info: { type: 'transfer', direction: 'from', walletName: wallet.name || '', walletId: wallet.id },
          comment: '',
          type: 'transfer',
        };
        batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, {
          balance: increment(-sum),
        });
        batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, newLog);
        batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${target}`, {
          balance: increment(sum - (bankCommission || 0)),
        });
        batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${target}/logs/${uniqid()}`, targetLog);
      } else {
        const log: WalletLog = {
          time: new Date().getTime(),
          sum: type === 'income' ? sum : -sum,
          workerId,
          workerName,
          category,
          bankCommission: type === 'income' ? bankCommission : 0,
          wallet: wallet.id,
          total: type === 'income' ? wallet.balance + sum - (bankCommission || 0) : wallet.balance - sum,
          info: { type: 'custom' },
          comment,
          type: 'custom',
        };
        batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, {
          balance: increment(type === 'income' ? sum - (bankCommission || 0) : -sum),
        });
        batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, log);
      }

      if (
        category &&
        !transactionCategories.includes(category as TransactionCategories) &&
        !(cafe?.customTransactionCategories || []).includes(category)
      ) {
        batch.update<Cafe>(`cafes/${cafeSlug}`, {
          customTransactionCategories: [...(cafe?.customTransactionCategories || []), category],
        });
      }

      batch.commit();
      onClose();
    },
    [
      cafe?.customTransactionCategories,
      cafeSlug,
      log,
      onClose,
      type,
      wallet.balance,
      wallet.id,
      wallet.name,
      walletsMap,
      workerId,
      workerName,
    ]
  );

  const cancelAction = useCallback(async () => {
    if (log) {
      cancelTransaction({
        log,
        cafeSlug,
        wallet,
        workerId,
        workerName,
      });
      onClose();
    }
  }, [cafeSlug, log, onClose, wallet, workerId, workerName]);

  useEffect(() => {
    if (sum && bankPercent) {
      form.setValue('bankCommission', getBankCommission(Number(sum), bankPercent));
    }
  }, [bankPercent, form, sum]);

  return (
    <EditModal<Form>
      open
      form={form}
      onClose={onClose}
      title={type ? t[type][lang] : ''}
      submitButton={!log && type ? `${t.add[lang]} ${t[type][lang]}` : t.save_changes[lang]}
      onSubmit={saveAction}
      secondaryButton={t.cancel[lang]}
      onSecondaryButtonClick={() => cancelAction()}
      secondaryButtonProps={{ disabled: !worker?.credentials.cancel_operations_with_wallets }}
      confirmText={t.cancel_wallet_action_confirm[lang]}
      size="xs"
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {type === 'transfer' && (
          <FormSelect
            name="target"
            label={t.to_wallet[lang]}
            options={wallets.filter((w) => w.id !== wallet.id).map((w) => w.id)}
            renderOption={(o) => walletsMap[o]?.name || ''}
            required
            rules={{ required: true }}
          />
        )}

        <FormPriceField
          name="sum"
          fullWidth
          label={t.amount[lang]}
          required
          disabled={!!log}
          InputProps={{
            startAdornment: !log && type ? <InputAdornment position="start">{icons[type]}</InputAdornment> : undefined,
            endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
          }}
          helperText={
            !log && (type === 'spending' || type === 'transfer')
              ? `${t.available[lang]}: ${getCurrency(wallet.balance)}`
              : undefined
          }
        />

        {type !== 'spending' && bankPercent && (
          <FormPriceField
            name="bankCommission"
            fullWidth
            label={t.bank_commission[lang]}
            disabled={!!log}
            InputProps={{
              startAdornment: <InputAdornment position="start">{icons.spending}</InputAdornment>,
              endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
            }}
          />
        )}

        {type !== 'transfer' && (
          <FormAutocomplete
            name="category"
            label={t.category[lang]}
            options={[...transactionCategories, ...(cafe?.customTransactionCategories || [])]}
            renderOption={(props, o) => (
              <Box component="li" {...props}>
                {t[o as keyof typeof t]?.[lang] || o}
              </Box>
            )}
            getOptionLabel={(o) => t[o as keyof typeof t]?.[lang] || o}
          />
        )}

        {log && <Typography>{getWalletLogComment(log.info, lang)}</Typography>}

        {type !== 'transfer' && (
          <FormTextField name="comment" fullWidth required label={t.comment[lang]} rules={{ required: true }} />
        )}
      </Box>
    </EditModal>
  );
};

export default EventModal;
